import axios from 'axios';
import { getClerkInstance } from '../auth/clerkAuth';

/**
 * Shared watchlist store.
 * Now supports both localStorage (offline/unauthenticated) and database (authenticated).
 */

const STORAGE_KEY = 'tc_watchlist_v1';

async function load() {
  const auth = getClerkInstance();
  if (auth && auth.isAuthenticated) {
    try {
      const token = await auth.getToken();
      const { data } = await axios.get('/api/watchlist', {
        headers: { Authorization: `Bearer ${token}` }
      });
      return data.watchlist;
    } catch (e) {
      console.error('[Watchlist] API load failed, falling back to local:', e);
    }
  }

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveLocal(list) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {}
}

/**
 * Build a normalised watchlist entry from a raw symbol item.
 * Returns null if the item cannot be resolved to a symbol.
 */
function buildEntry(item) {
  // ... (keeping existing buildEntry logic)
  // Coinbase/Kraken/Bybit/OKX/AMFI items are also tagged type "Crypto Spot"
  // (and AMFI "Mutual Fund"), so they must be told apart by exchange BEFORE
  // isBinanceCrypto — same fix as Chart.vue's onSelect().
  const isCoinbase = item.exchange === 'Coinbase';
  const isKraken = item.exchange === 'Kraken';
  const isBybit = item.exchange === 'Bybit';
  const isOkx = item.exchange === 'OKX';
  const isAmfi = item.exchange === 'AMFI';

  const isBinanceCrypto =
    !isCoinbase && !isKraken && !isBybit && !isOkx && !isAmfi && (
    item.type === 'Crypto Spot' ||
    item.type === 'Crypto Futures' ||
    (item.exchange === 'Binance' && !item.fyToken && !item.instrument_key));

  const isFyers =
    (item.exchange === 'Fyers' || item.type === 'Fyers' || !!item.fyToken) &&
    !isBinanceCrypto;

  const isUpstox =
    (item.exchange === 'Upstox' || item.type === 'Upstox' || !!item.instrument_key) &&
    !isBinanceCrypto &&
    !isFyers &&
    item.type !== 'IndianMarket';

  const isAliceBlue =
    (item.exchange === 'AliceBlue' || item.type === 'AliceBlue' || !!item.token) &&
    !isBinanceCrypto &&
    !isFyers &&
    !isUpstox &&
    item.type !== 'IndianMarket';

  const isIndianMarket = item.type === 'IndianMarket';

  let symbol, pairId, connector, exchange, category, name, segment;

  if (isCoinbase || isBybit || isOkx) {
    symbol = item.symbol;
    pairId = item.symbol;
    connector = item.exchange;
    exchange = item.exchange;
    category = item.type || 'Crypto Spot';
    name = item.name || symbol;
    segment = item.exchange;
  } else if (isKraken) {
    symbol = item.symbol;
    pairId = item.wsname || item.symbol;
    connector = 'Kraken';
    exchange = 'Kraken';
    category = item.type || 'Crypto Spot';
    name = item.name || symbol;
    segment = 'Kraken';
  } else if (isAmfi) {
    symbol = item.symbol;
    pairId = item.symbol;
    connector = 'AMFI';
    exchange = 'AMFI';
    category = item.type || 'Mutual Fund';
    name = item.name || symbol;
    segment = 'AMFI';
  } else if (isBinanceCrypto) {
    symbol = item.contractType === 'PERPETUAL' ? item.symbol + '.P' : item.symbol;
    pairId = symbol;
    connector = 'Binance';
    exchange = 'Binance';
    category = item.type || 'Crypto Spot';
    name = item.baseAsset ? item.baseAsset + '/' + (item.quoteAsset || '') : symbol;
    segment = item.type === 'Crypto Futures' ? 'Futures' : 'Spot';
  } else if (isFyers) {
    symbol = item.symbol || item.trading_symbol;
    pairId = item.fyToken || symbol;
    connector = 'Fyers';
    exchange = item.exch || 'NSE';
    category = item.type || 'Fyers';
    name = item.formatted_ins_name || item.name || symbol;
    segment = item.exch || 'NSE';
  } else if (isUpstox) {
    symbol = item.trading_symbol || item.symbol;
    pairId = item.instrument_key || symbol;
    connector = 'Upstox';
    exchange = item.exch || item.exchange || 'NSE';
    category = item.type || 'Upstox';
    name = item.formatted_ins_name || item.name || symbol;
    segment = item.exch || item.exchange || 'NSE';
  } else if (isAliceBlue) {
    symbol = item.trading_symbol || item.symbol;
    pairId = item.token || symbol;
    connector = 'AliceBlue';
    exchange = item.exch || item.exchange || 'NSE';
    category = item.type || 'AliceBlue';
    name = item.formatted_ins_name || item.name || symbol;
    segment = item.exch || item.exchange || 'NSE';
  } else if (isIndianMarket) {
    symbol = item.trading_symbol || item.symbol;
    pairId = item.instrument_key;
    connector = 'Indian';
    exchange = item.exch || item.exchange || 'NSE';
    category = 'IndianMarket';
    name = item.formatted_ins_name || item.name || symbol;
    segment = item.exch || item.exchange || 'NSE';
  } else if (item.pairId) {
    symbol = item.symbol;
    pairId = item.pairId;
    connector = 'Int';
    exchange = item.exchange;
    category = item.type || '';
    name = item.name || symbol;
    segment = item.exchange || 'Int';
  } else {
    symbol = item.trading_symbol || item.symbol;
    pairId = item.token;
    connector = 'Indian';
    exchange = item.exch;
    category = item.exchange_segment || '';
    name = item.formatted_ins_name || item.name || symbol;
    segment = item.exch || 'NSE';
  }

  if (!symbol) return null;

  return {
    key: symbol + '::' + connector + '::' + exchange,
    symbol,
    pairId,
    connector,
    exchange,
    category,
    name: name || symbol,
    segment,
    _item: item,
  };
}

/** Return the watchlist key for a raw symbol item (without building the full entry). */
function keyFor(item) {
  const entry = buildEntry(item);
  return entry ? entry.key : null;
}

/** Check whether a raw symbol item is already in the watchlist. */
async function isInWatchlist(item) {
  const key = keyFor(item);
  if (!key) return false;
  return (await load()).some(e => e.key === key);
}

/** Add a raw symbol item to the watchlist. Returns true if it was added. */
async function addItem(item) {
  const entry = buildEntry(item);
  if (!entry) return false;

  const auth = getClerkInstance();
  if (auth && auth.isAuthenticated) {
    try {
      const token = await auth.getToken();
      await axios.post('/api/watchlist', { action: 'add', item: entry }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return true;
    } catch (e) {
      console.error('[Watchlist] API add failed:', e);
    }
  }

  const list = await load();
  if (list.some(e => e.key === entry.key)) return false;
  list.push(entry);
  saveLocal(list);
  return true;
}

/** Remove a raw symbol item from the watchlist. Returns true if it was removed. */
async function removeItem(item) {
  // Support both raw items and pre-built entries (which already have a .key)
  const key = item.key || keyFor(item);
  if (!key) return false;

  const auth = getClerkInstance();
  if (auth && auth.isAuthenticated) {
    try {
      const token = await auth.getToken();
      await axios.post('/api/watchlist', { action: 'remove', item: { key } }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return true;
    } catch (e) {
      console.error('[Watchlist] API remove failed:', e);
    }
  }

  const list = await load();
  const next = list.filter(e => e.key !== key);
  if (next.length === list.length) return false;
  saveLocal(next);
  return true;
}

/** Toggle a raw symbol item in the watchlist. Returns true if now in list. */
async function toggleItem(item) {
  if (await isInWatchlist(item)) {
    await removeItem(item);
    return false;
  } else {
    await addItem(item);
    return true;
  }
}

export default {
  load,
  buildEntry,
  keyFor,
  isInWatchlist,
  addItem,
  removeItem,
  toggleItem,
};
